<template>
  <v-dialog
    :model-value="areaDialog"
    persistent
    width="70%"
    class="py-5"
    @update:model-value="(value) => emit('update:areaDialog', value)"
  >
    <v-card>
      <v-card-title>{{ title || wordList.lbl_address || "住所選択" }}</v-card-title>
      <v-card-text>
        <v-divider />
        <v-row class="font-weight-black text-h6 py-2">
          <v-col>
            {{ wordList.lbl_address || "住所" }} ： {{ selectedAddress }}
          </v-col>
        </v-row>
        <v-divider />

        <v-row v-if="cityBlockList && !sectionBlockList" dense>
          <v-col class="py-2 pl-0">
            <template v-for="group in kanaGroups" :key="group.key">
              <v-card v-if="getKanaItems(cityBlockList, group.key).length > 0" :flat="true">
                <v-card-text class="pb-0">{{ group.label }}</v-card-text>
                <v-btn
                  v-for="value in getKanaItems(cityBlockList, group.key)"
                  :key="`city-${group.key}-${value}`"
                  class="my-2 mx-2"
                  color="primary"
                  variant="outlined"
                  min-width="200"
                  :disabled="clickDisabled"
                  @click="emit('selectCity', value)"
                >
                  {{ value }}
                </v-btn>
              </v-card>
            </template>
          </v-col>
        </v-row>

        <v-row v-if="sectionBlockList && blockList.length === 0" dense>
          <v-col class="py-2 pl-0">
            <template v-for="group in kanaGroups" :key="group.key">
              <v-card v-if="getKanaItems(sectionBlockList, group.key).length > 0" :flat="true">
                <v-card-text class="pb-0">{{ group.label }}</v-card-text>
                <v-btn
                  v-for="value in getKanaItems(sectionBlockList, group.key)"
                  :key="`section-${group.key}-${value}`"
                  class="mx-3 my-2"
                  color="primary"
                  variant="outlined"
                  min-width="200"
                  :disabled="clickDisabled"
                  @click="emit('selectSection', value)"
                >
                  {{ value }}
                </v-btn>
              </v-card>
            </template>
          </v-col>
        </v-row>

        <v-row v-if="blockList.length > 0" dense>
          <v-col class="py-2">
            <v-card :flat="true">
              <v-btn
                v-for="block in blockList"
                :key="`block-${String(block)}`"
                class="mx-3 my-2"
                color="primary"
                variant="outlined"
                min-width="200"
                :disabled="clickDisabled"
                @click="emit('selectBlock', String(block ?? ''))"
              >
                {{ block ?? "" }}
              </v-btn>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import type { KanaStepA } from "./types";

/** カナ行キーを表す型。 */
type KanaGroupKey =
  | "line_a"
  | "line_ka"
  | "line_sa"
  | "line_ta"
  | "line_na"
  | "line_ha"
  | "line_ma"
  | "line_ya"
  | "line_ra"
  | "line_wa"
  | "line_other";

/** コンポーネント入力を表す型。 */
type AreaSelectorDialogProps = {
  areaDialog: boolean;
  title?: string;
  selectedAddress: string;
  cityBlockList: KanaStepA | null;
  sectionBlockList: KanaStepA | null;
  blockList: Array<string | null | undefined>;
  clickDisabled: boolean;
  wordList: Record<string, string>;
};

/** コンポーネント出力イベントを表す型。 */
type AreaSelectorDialogEmits = {
  (e: "update:areaDialog", value: boolean): void;
  (e: "selectCity", value: string): void;
  (e: "selectSection", value: string): void;
  (e: "selectBlock", value: string): void;
};

const emit = defineEmits<AreaSelectorDialogEmits>();
defineProps<AreaSelectorDialogProps>();

/** カナ行表示ラベル定義を表す定数。 */
const kanaGroups: Array<{ key: KanaGroupKey; label: string }> = [
  { key: "line_a", label: "あ行" },
  { key: "line_ka", label: "か行" },
  { key: "line_sa", label: "さ行" },
  { key: "line_ta", label: "た行" },
  { key: "line_na", label: "な行" },
  { key: "line_ha", label: "は行" },
  { key: "line_ma", label: "ま行" },
  { key: "line_ya", label: "や行" },
  { key: "line_ra", label: "ら行" },
  { key: "line_wa", label: "わ行" },
  { key: "line_other", label: "その他" },
];

/** カナ行ごとの候補を取得する。 */
const getKanaItems = (list: KanaStepA | null, key: KanaGroupKey): string[] => {
  if (!list) {
    return [];
  }

  return (list[key] ?? []).filter((value): value is string => typeof value === "string" && value.length > 0);
};
</script>
